<template>
  <div :class="embedded ? '' : 'py-3 py-md-4 pb-5 mb-5'">

    <div v-if="!embedded" class="d-flex align-items-start justify-content-between flex-wrap mb-3" style="gap:10px;">
      <div>
        <h3 class="text-primary font-weight-bold mb-0">Debtors</h3>
        <div class="text-secondary small">{{ canViewAll ? 'Everything customers owe you: BNPL, memberships, bookings and invoices' : 'Balances on the sales and bookings you made' }}</div>
      </div>
      <a href="/bnpl" class="btn btn-outline-secondary"><i class="bi bi-wallet2 mr-1"></i>BNPL</a>
    </div>

    <!-- Figures -->
    <div v-if="canViewAll && summary" class="card mb-3">
      <div class="card-body">
        <div class="row">
          <div class="col-6 col-md mb-3 mb-md-0">
            <div class="small text-secondary">Customers owe you</div>
            <div class="h5 font-weight-bold text-dark mb-0">{{ money(summary.outstanding) }}</div>
            <div class="small text-secondary">{{ summary.customers }} customer{{ summary.customers === 1 ? '' : 's' }} · {{ summary.debts }} balance{{ summary.debts === 1 ? '' : 's' }}</div>
          </div>
          <a href="#" class="col-6 col-md mb-3 mb-md-0 text-decoration-none" @click.prevent="setStatus('overdue')">
            <div class="small text-secondary">Overdue</div>
            <div class="h5 font-weight-bold mb-0" :class="summary.overdue_amount > 0 ? 'text-danger' : 'text-dark'">{{ money(summary.overdue_amount) }}</div>
            <div class="small text-secondary">{{ summary.overdue_count }} balance{{ summary.overdue_count === 1 ? '' : 's' }}</div>
          </a>
          <a href="#" class="col-6 col-md mb-3 mb-md-0 text-decoration-none" @click.prevent="setStatus('due_soon')">
            <div class="small text-secondary">Due in 7 days</div>
            <div class="h5 font-weight-bold text-dark mb-0">{{ money(summary.due_soon_amount) }}</div>
            <div class="small text-secondary">{{ summary.due_soon_count }} balance{{ summary.due_soon_count === 1 ? '' : 's' }}</div>
          </a>
          <div class="col-6 col-md">
            <div class="small text-secondary mb-1">By type</div>
            <a v-for="(t, key) in summary.by_type" :key="key" href="#" class="d-flex justify-content-between small text-decoration-none" @click.prevent="setType(key)">
              <span class="text-secondary">{{ typeLabel(key) }}</span><span class="text-dark">{{ money(t.balance) }}</span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Status + type -->
    <div class="d-flex flex-wrap align-items-center mb-3" style="gap:8px;">
      <ul class="nav nav-pills flex-nowrap overflow-auto mb-0" style="gap:4px;">
        <li v-for="t in statusTabs" :key="t.key" class="nav-item">
          <a href="#" class="nav-link text-nowrap" :class="{ active: view === 'list' && status === t.key }" @click.prevent="setStatus(t.key)">
            {{ t.label }}<span v-if="t.count !== null" class="badge ml-1" :class="view === 'list' && status === t.key ? 'badge-light' : (t.key === 'overdue' && t.count ? 'badge-danger' : 'badge-secondary')">{{ t.count }}</span>
          </a>
        </li>
        <li class="nav-item">
          <a href="#" class="nav-link text-nowrap" :class="{ active: view === 'customers' }" @click.prevent="setView('customers')">By customer</a>
        </li>
      </ul>
      <select v-model="type" class="custom-select w-auto ml-md-auto" aria-label="Type" @change="reload">
        <option value="">All types</option>
        <option v-for="t in types" :key="t" :value="t">{{ typeLabel(t) }}</option>
      </select>
    </div>

    <!-- Filters -->
    <div class="card mb-3">
      <div class="card-body pb-2">
        <div class="form-row">
          <div class="form-group col-12 col-lg-5">
            <div class="input-group">
              <div class="input-group-prepend"><span class="input-group-text"><i class="bi bi-search"></i></span></div>
              <input v-model="q" type="search" class="form-control" placeholder="Customer, phone, order / invoice / booking #" aria-label="Search" @input="debouncedReload">
            </div>
          </div>
          <div class="form-group col-12 col-sm-6 col-lg-3">
            <date-range-picker v-model="dueRange" :auto-apply="true" :ranges="false" :append-to-body="true" opens="left" class="w-100" @update="onDueRange">
              <template #input="picker">
                <span v-if="picker.startDate">Due {{ fmtShort(picker.startDate) }} - {{ fmtShort(picker.endDate) }}</span>
                <span v-else class="text-secondary"><i class="bi bi-calendar-event mr-1"></i>Due date</span>
              </template>
            </date-range-picker>
          </div>
          <div class="form-group col-6 col-sm-3 col-lg-2">
            <select v-model="sortKey" class="custom-select" aria-label="Sort" @change="reload">
              <option value="">Needs attention first</option>
              <option value="days_overdue:desc">Most days overdue</option>
              <option value="balance:desc">Highest balance</option>
              <option value="due_date:asc">Due date</option>
              <option value="customer:asc">Customer A-Z</option>
            </select>
          </div>
          <div class="form-group col-6 col-sm-3 col-lg-2 d-flex align-items-center">
            <button class="btn btn-link text-secondary px-0" @click="resetFilters"><i class="bi bi-x-circle mr-1"></i>Reset</button>
          </div>
        </div>
        <div v-if="customerChip" class="mb-2">
          <span class="badge badge-dark p-2">Customer: {{ customerChip }} <a href="#" class="text-white ml-1" aria-label="Clear customer filter" @click.prevent="clearCustomer"><i class="bi bi-x"></i></a></span>
        </div>
      </div>
    </div>

    <div v-if="loading && !rows.length && !customers.length" class="card card-body text-center py-5"><div class="spinner-border text-primary mx-auto"></div></div>
    <div v-else-if="error" class="alert alert-danger">{{ error }} <a href="#" @click.prevent="reload">Retry</a></div>

    <!-- By customer -->
    <template v-else-if="view === 'customers'">
      <div v-if="!customers.length" class="card card-body text-center text-secondary py-5">Nobody owes anything here.</div>
      <div v-else class="card">
        <div v-for="c in customers" :key="c.customer_id || c.name" class="d-flex align-items-center justify-content-between flex-wrap px-3 py-3 border-bottom" style="gap:10px;">
          <a href="#" class="text-decoration-none" style="min-width:0;" @click.prevent="filterCustomer(c)">
            <div class="font-weight-bold text-dark text-truncate">{{ c.name || 'Customer' }}</div>
            <div class="small text-secondary">{{ c.phone }} · {{ c.debts }} balance{{ c.debts === 1 ? '' : 's' }}
              <span v-for="(amt, t) in c.types" :key="t" class="badge badge-secondary ml-1">{{ typeLabel(t) }} {{ money(amt) }}</span>
            </div>
          </a>
          <div class="d-flex align-items-center">
            <div class="text-right">
              <div class="font-weight-bold" :class="c.overdue > 0 ? 'text-danger' : 'text-dark'">{{ money(c.balance) }}</div>
              <div v-if="c.overdue > 0" class="small text-danger">{{ money(c.overdue) }} overdue</div>
            </div>
            <div class="dropdown d-inline-block">
              <a href="#" data-toggle="dropdown" data-boundary="viewport" aria-expanded="false" class="btn btn-link text-secondary px-2 py-0" aria-label="Actions"><i class="bi bi-three-dots-vertical"></i></a>
              <div class="dropdown-menu dropdown-menu-right">
                <a href="#" class="dropdown-item" @click.prevent="filterCustomer(c)"><i class="bi bi-list-ul mr-2"></i>View their balances</a>
                <template v-if="digits(c.phone)">
                  <div class="dropdown-divider"></div>
                  <a :href="'tel:' + c.phone" class="dropdown-item"><i class="bi bi-telephone mr-2"></i>Call</a>
                  <a :href="'https://wa.me/' + digits(c.phone)" target="_blank" rel="noopener" class="dropdown-item"><i class="bi bi-whatsapp mr-2"></i>WhatsApp</a>
                </template>
                <template v-if="c.customer_id">
                  <div class="dropdown-divider"></div>
                  <a :href="'/customer?id=' + c.customer_id" class="dropdown-item"><i class="bi bi-person mr-2"></i>Customer profile</a>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- List -->
    <template v-else>
      <div v-if="!rows.length" class="card card-body text-center text-secondary py-5">
        <i class="bi d-block mb-2" :class="status === 'overdue' ? 'bi-emoji-smile' : 'bi-inbox'" style="font-size:2rem;"></i>
        {{ status === 'overdue' ? 'Nothing overdue.' : 'Nothing owed here.' }}
      </div>
      <template v-else>
        <div class="card d-none d-md-block">
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead class="thead-light">
                <tr><th>Customer</th><th>For</th><th>Due</th><th class="text-right">Owed</th><th style="width:56px;"><span class="sr-only">Actions</span></th></tr>
              </thead>
              <tbody>
                <tr v-for="r in rows" :key="r.key" style="cursor:pointer;" @click="onRowClick(r, $event)">
                  <td>
                    <div class="font-weight-bold text-dark">{{ r.customer.name || 'Customer' }}</div>
                    <div class="small text-secondary">{{ r.customer.phone }}</div>
                  </td>
                  <td>
                    <span class="badge badge-secondary mr-1">{{ typeLabel(r.type) }}</span><span class="text-dark">{{ r.reference }}</span>
                    <div class="small text-secondary">{{ r.description }}</div>
                  </td>
                  <td>
                    <div class="text-dark">{{ r.due_date ? fmtDate(r.due_date) : '—' }}</div>
                    <span class="badge" :class="statusClass(r)">{{ statusLabel(r) }}</span>
                  </td>
                  <td class="text-right">
                    <div class="font-weight-bold" :class="r.status === 'overdue' ? 'text-danger' : 'text-dark'">{{ money(r.balance) }}</div>
                    <div v-if="r.total" class="small text-secondary">of {{ money(r.total) }}</div>
                  </td>
                  <td class="text-right">
                    <div class="dropdown d-inline-block">
                      <a href="#" data-toggle="dropdown" data-boundary="viewport" aria-expanded="false" class="btn btn-link text-secondary px-2 py-0" aria-label="Actions"><i class="bi bi-three-dots-vertical"></i></a>
                      <div class="dropdown-menu dropdown-menu-right">
                        <template v-for="(act, i) in actions(r)">
                          <div v-if="act.divider" :key="'d' + i" class="dropdown-divider"></div>
                          <a v-else :key="'a' + i" :href="act.href || '#'" :target="act.target || null" :rel="act.target ? 'noopener' : null" class="dropdown-item" :class="{ 'text-danger': act.danger }" @click="runAction(act, $event)">
                            <i class="bi mr-2" :class="act.icon"></i>{{ act.label }}
                          </a>
                        </template>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="d-md-none">
          <div v-for="r in rows" :key="'m' + r.key" class="card card-body mb-2 p-3" style="cursor:pointer;" @click="onRowClick(r, $event)">
            <div class="d-flex justify-content-between" style="gap:10px;">
              <div style="min-width:0;">
                <div class="font-weight-bold text-dark text-truncate">{{ r.customer.name || 'Customer' }}</div>
                <div class="small text-secondary"><span class="badge badge-secondary mr-1">{{ typeLabel(r.type) }}</span>{{ r.reference }} · due {{ r.due_date ? fmtDate(r.due_date) : '—' }}</div>
                <span class="badge mt-1" :class="statusClass(r)">{{ statusLabel(r) }}</span>
              </div>
              <div class="d-flex align-items-start">
                <div class="text-right">
                  <div class="font-weight-bold" :class="r.status === 'overdue' ? 'text-danger' : 'text-dark'">{{ money(r.balance) }}</div>
                  <div v-if="r.total" class="small text-secondary">of {{ money(r.total) }}</div>
                </div>
                <div class="dropdown d-inline-block">
                  <a href="#" data-toggle="dropdown" data-boundary="viewport" aria-expanded="false" class="btn btn-link text-secondary px-2 py-0" aria-label="Actions"><i class="bi bi-three-dots-vertical"></i></a>
                  <div class="dropdown-menu dropdown-menu-right">
                    <template v-for="(act, i) in actions(r)">
                      <div v-if="act.divider" :key="'d' + i" class="dropdown-divider"></div>
                      <a v-else :key="'a' + i" :href="act.href || '#'" :target="act.target || null" :rel="act.target ? 'noopener' : null" class="dropdown-item" :class="{ 'text-danger': act.danger }" @click="runAction(act, $event)">
                        <i class="bi mr-2" :class="act.icon"></i>{{ act.label }}
                      </a>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex align-items-center justify-content-between flex-wrap mt-3" style="gap:10px;">
          <span class="text-secondary small">{{ meta.total }} balance{{ meta.total === 1 ? '' : 's' }} · {{ money(meta.balance) }}</span>
          <b-pagination v-if="meta.last_page > 1" v-model="page" :total-rows="meta.total" :per-page="meta.per_page" class="mb-0" @change="goPage" />
        </div>
      </template>
    </template>

    <!-- Record payment (each type through its own module's endpoint) -->
    <b-modal v-model="pay.show" title="Record payment" centered ok-title="Record payment" :busy="pay.busy" cancel-title="Back" @ok.prevent="submitPay">
      <template v-if="pay.row">
        <p class="text-secondary small mb-3">
          <span class="badge badge-secondary mr-1">{{ typeLabel(pay.row.type) }}</span>{{ pay.row.customer.name }} · {{ pay.row.reference }} · owes <strong class="text-dark">{{ money(pay.row.balance) }}</strong>
        </p>
        <div class="form-group">
          <label for="debt-pay-amount">Amount</label>
          <input id="debt-pay-amount" v-model.number="pay.amount" type="number" min="1" step="any" class="form-control">
          <small v-if="pay.row.type !== 'bnpl'" class="text-secondary">Up to the balance. (Overpayments to wallet credit are available on BNPL.)</small>
        </div>
        <div v-if="pay.row.type === 'bnpl'" class="form-group">
          <label>Date received</label>
          <date-range-picker v-if="canBackdate" v-model="pay.date" :single-date-picker="true" :auto-apply="true" :ranges="false" :append-to-body="true" :max-date="today" class="w-100">
            <template #input="picker">
              <span v-if="picker.startDate">{{ fmtDate(picker.startDate) }}</span>
              <span v-else class="text-secondary">Today</span>
            </template>
          </date-range-picker>
          <input v-else type="text" class="form-control" value="Today" readonly aria-label="Date received">
        </div>
        <div class="form-group mb-0">
          <label for="debt-pay-method">Paid with</label>
          <select id="debt-pay-method" v-model="pay.method" class="custom-select">
            <option :value="null" disabled>Choose payment method</option>
            <option v-for="m in paymentMethods" :key="m.id" :value="m.id">{{ m.payment_method }}</option>
          </select>
        </div>
        <p v-if="pay.error" class="text-danger small mt-3 mb-0">{{ pay.error }}</p>
      </template>
    </b-modal>

    <b-modal v-model="confirm.show" :title="confirm.title" centered :ok-title="confirm.okTitle" :ok-variant="confirm.variant" :busy="confirm.busy" cancel-title="Back" @ok.prevent="confirm.run">
      <p class="mb-2">{{ confirm.text }}</p>
      <input v-if="confirm.needsReason" v-model="confirm.reason" type="text" maxlength="255" class="form-control" placeholder="Reason" aria-label="Reason">
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker'

const TYPE_LABELS = { bnpl: 'BNPL', membership: 'Membership', booking: 'Booking', invoice: 'Invoice' }
const MODULE_LABELS = { bnpl: 'Open in BNPL', membership: 'Open membership', booking: 'Open booking', invoice: 'Open invoice' }

/**
 * Debtors: every customer balance (BNPL, memberships, bookings, invoices).
 * Data: /api/debtors (DebtorsService). Payments go to each module's own
 * endpoint so receipts, GL and reports are exactly as from that module.
 */
export default {
  name: 'DebtorsPage',

  components: { DateRangePicker },

  // Shown as the "All debtors" tab inside BNPL / Debtors (no own header).
  props: { embedded: { type: Boolean, default: false } },

  data() {
    return {
      view: 'list',
      status: 'overdue',
      type: '',
      q: '',
      sortKey: '',
      customerId: null,
      customerChip: null,
      dueRange: { startDate: null, endDate: null },
      dueFrom: null,
      dueTo: null,
      page: 1,
      rows: [],
      customers: [],
      meta: { current_page: 1, last_page: 1, per_page: 25, total: 0, balance: 0 },
      summary: null,
      loading: false,
      error: null,
      paymentMethods: [],
      today: moment().format('YYYY-MM-DD'),
      pay: { show: false, row: null, amount: null, method: null, date: { startDate: null, endDate: null }, busy: false, error: null },
      confirm: { show: false, title: '', text: '', okTitle: 'OK', variant: 'primary', busy: false, needsReason: false, reason: '', run: () => {} },
    }
  },

  computed: {
    ...mapGetters(['userDetails']),
    isAdmin() { return (this.userDetails.roles || []).some(r => r.name === 'Admin' || r.name === 'owner') },
    canViewAll() { return this.isAdmin || this.has('view-orders') || this.has('can manage orders') },
    canBackdate() { return this.isAdmin || this.has('can manage orders') },
    canRemind() { return this.isAdmin || this.has('can manage orders') },
    canWaive() { return this.isAdmin || this.has('void-orders') },
    types() {
      return this.summary ? Object.keys(this.summary.by_type) : ['bnpl', 'membership', 'booking', 'invoice']
    },
    statusTabs() {
      const s = this.summary
      return [
        { key: 'overdue', label: 'Overdue', count: s ? s.overdue_count : null },
        { key: 'due_soon', label: 'Due soon', count: s ? s.due_soon_count : null },
        { key: 'all', label: 'All', count: s ? s.debts : null },
      ]
    },
  },

  mounted() {
    this._t = null
    if (this.canViewAll) this.loadSummary()
    else this.status = 'all'
    this.reload()
  },

  methods: {
    has(p) { return (this.userDetails.permissions || []).some(x => x.name === p) },

    params() {
      const [sort, dir] = (this.sortKey || '').split(':')
      const p = { status: this.status, type: this.type, q: this.q, customer_id: this.customerId, due_from: this.dueFrom, due_to: this.dueTo, page: this.page, per_page: 25 }
      if (sort) { p.sort = sort; p.dir = dir }
      Object.keys(p).forEach(k => (p[k] === '' || p[k] === null) && delete p[k])
      return p
    },
    async loadSummary() {
      try { this.summary = (await axios.get('/api/debtors/summary')).data } catch (e) { this.summary = null }
    },
    async reload() {
      this.loading = true
      this.error = null
      try {
        if (this.view === 'customers') {
          const { status, page, per_page, ...p } = this.params()
          this.customers = (await axios.get('/api/debtors/customers', { params: { ...p, status: 'all' } })).data
        } else {
          const { data } = await axios.get('/api/debtors', { params: this.params() })
          this.rows = data.data
          this.meta = data.meta
          this.page = data.meta.current_page
        }
      } catch (e) {
        this.error = 'Could not load debtors.'
      } finally {
        this.loading = false
      }
    },
    debouncedReload() {
      clearTimeout(this._t)
      this._t = setTimeout(() => { this.page = 1; this.reload() }, 300)
    },
    setStatus(s) { this.view = 'list'; this.status = s; this.page = 1; this.reload() },
    setView(v) { this.view = v; this.page = 1; this.reload() },
    setType(t) { this.type = t; this.view = 'list'; this.status = 'all'; this.page = 1; this.reload() },
    goPage(p) { this.page = p; this.reload() },
    onDueRange() {
      this.dueFrom = this.dueRange.startDate ? moment(this.dueRange.startDate).format('YYYY-MM-DD') : null
      this.dueTo = this.dueRange.endDate ? moment(this.dueRange.endDate).format('YYYY-MM-DD') : null
      this.page = 1
      this.reload()
    },
    resetFilters() {
      Object.assign(this, { q: '', type: '', sortKey: '', customerId: null, customerChip: null, dueFrom: null, dueTo: null, dueRange: { startDate: null, endDate: null }, page: 1 })
      this.reload()
    },
    filterCustomer(c) {
      this.customerId = c.customer_id || (c.customer && c.customer.id) || null
      this.customerChip = c.name || (c.customer && c.customer.name) || 'Customer'
      this.view = 'list'
      this.status = 'all'
      this.page = 1
      this.reload()
    },
    clearCustomer() { this.customerId = null; this.customerChip = null; this.page = 1; this.reload() },

    onRowClick(r, e) {
      if (e.target.closest('.dropdown, a, button, input')) return
      if (r.can_pay) this.openPay(r)
      else window.location.href = r.link
    },
    runAction(act, e) {
      if (act.run) { e.preventDefault(); act.run() }
    },
    actions(r) {
      const list = []
      if (r.can_pay) list.push({ label: 'Record payment', icon: 'bi-cash-coin', run: () => this.openPay(r) })
      if (r.type === 'bnpl' && this.canRemind) list.push({ label: 'Send SMS reminder', icon: 'bi-chat-dots', run: () => this.remindBnpl(r) })
      if (r.type === 'bnpl' && this.canWaive) list.push({ label: 'Waive balance', icon: 'bi-x-octagon', run: () => this.waiveBnpl(r) })
      list.push({ label: MODULE_LABELS[r.type], icon: 'bi-box-arrow-up-right', href: r.link })
      const phone = this.digits(r.customer.phone)
      if (phone) {
        list.push({ divider: true })
        list.push({ label: 'Call ' + (r.customer.name || 'customer'), icon: 'bi-telephone', href: 'tel:' + r.customer.phone })
        list.push({ label: 'WhatsApp', icon: 'bi-whatsapp', target: '_blank', href: `https://wa.me/${phone}?text=${encodeURIComponent(`Hi ${(r.customer.name || '').split(' ')[0]}, a reminder: you owe ${this.money(r.balance)} for ${TYPE_LABELS[r.type].toLowerCase()} ${r.reference}.`)}` })
      }
      if (r.customer.id) {
        list.push({ divider: true })
        list.push({ label: 'Everything this customer owes', icon: 'bi-funnel', run: () => this.filterCustomer(r) })
        list.push({ label: 'Customer profile', icon: 'bi-person', href: '/customer?id=' + r.customer.id })
      }
      return list
    },

    async ensureMethods() {
      if (this.paymentMethods.length) return
      try {
        const { data } = await axios.get('/api/payments')
        this.paymentMethods = (Array.isArray(data) ? data : []).filter(m => m.enabled !== false && !/later|bnpl/i.test(m.payment_method || ''))
      } catch (e) { this.paymentMethods = [] }
    },
    async openPay(r) {
      await this.ensureMethods()
      this.pay = { show: true, row: r, amount: r.balance, method: this.paymentMethods.length === 1 ? this.paymentMethods[0].id : null, date: { startDate: new Date(), endDate: new Date() }, busy: false, error: null }
    },
    async submitPay() {
      const p = this.pay
      const r = p.row
      if (!(p.amount > 0) || !p.method) { p.error = 'Enter an amount and choose how it was paid.'; return }
      if (r.type !== 'bnpl' && p.amount > r.balance + 0.009) { p.error = 'That is more than the balance.'; return }
      const body = { amount: p.amount, payment_type_id: p.method }
      const url = {
        bnpl: `/api/bnpl/accounts/${r.id}/payments`,
        booking: `/api/reservations/payment/${r.id}`,
        invoice: `/api/invoices/${r.id}/payments`,
        membership: `/api/beta-pay/${r.id}/payment`,
      }[r.type]
      if (r.type === 'bnpl' && p.date.startDate && !moment(p.date.startDate).isSame(moment(), 'day')) body.received_on = moment(p.date.startDate).format('YYYY-MM-DD')
      p.busy = true
      p.error = null
      try {
        const { data } = await axios.post(url, body)
        p.show = false
        this.toast('success', 'Payment recorded.' + (data && data.credited > 0 ? ` ${this.money(data.credited)} went to the customer's wallet.` : ''))
        this.refresh()
      } catch (e) {
        const errs = e.response?.data?.errors
        p.error = errs ? Object.values(errs).flat()[0] : (e.response?.data?.message || 'Could not record the payment.')
      } finally {
        p.busy = false
      }
    },
    remindBnpl(r) {
      this.ask('Send reminder', `Send an SMS reminder to ${r.customer.name} about ${this.money(r.balance)}?`, 'Send SMS', 'primary', false, async () => {
        await axios.post(`/api/bnpl/accounts/${r.id}/remind`)
        this.toast('success', 'Reminder sent.')
      })
    },
    waiveBnpl(r) {
      this.ask('Waive balance', `Waive the remaining ${this.money(r.balance)} on ${r.reference}? It's recorded as a bad-debt expense with your name and reason.`, 'Waive', 'danger', true, async () => {
        if ((this.confirm.reason || '').trim().length < 3) throw { response: { data: { message: 'Add a short reason.' } } }
        await axios.post('/api/bnpl/waive', { ids: [r.id], amount: null, reason: this.confirm.reason.trim() })
        this.toast('success', 'Balance waived.')
        this.refresh()
      })
    },
    ask(title, text, okTitle, variant, needsReason, fn) {
      this.confirm = {
        show: true, title, text, okTitle, variant, needsReason, reason: '', busy: false,
        run: async () => {
          this.confirm.busy = true
          try { await fn(); this.confirm.show = false } catch (e) {
            const errs = e.response?.data?.errors
            this.toast('error', errs ? Object.values(errs).flat()[0] : (e.response?.data?.reason || e.response?.data?.message || 'That did not work.'))
          } finally { this.confirm.busy = false }
        },
      }
    },
    refresh() {
      if (this.canViewAll) this.loadSummary()
      this.reload()
    },

    typeLabel(t) { return TYPE_LABELS[t] || t },
    statusLabel(r) {
      if (r.status === 'overdue') return r.days_overdue ? `Overdue ${r.days_overdue}d` : 'Overdue'
      if (r.status === 'due_today') return 'Due today'
      if (r.status === 'due_soon') return 'Due soon'
      return r.due_date ? 'Open' : 'No due date'
    },
    statusClass(r) { return { overdue: 'badge-danger', due_today: 'badge-primary', due_soon: 'badge-primary' }[r.status] || 'badge-dark' },
    digits(p) { return (p || '').replace(/[^0-9]/g, '') },
    toast(type, message) { this.$toasts && this.$toasts.push({ type, message }) },
    money(v) { return 'KES ' + Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 2 }) },
    fmtDate(d) { return d ? moment(d).format('D MMM YYYY') : '—' },
    fmtShort(d) { return d ? moment(d).format('D MMM') : '' },
  },
}
</script>
